import { supabase } from "./supabase";
import type {
  RoutineJob,
  RoutineReview,
  RoutineSnapshot,
} from "./routineTypes";
export class RoutineApiError extends Error {
  constructor(
    message: string,
    public status?: number,
  ) {
    super(message);
  }
}

let recovery: Promise<string | null> | null = null;
async function recoverSession(rejectedToken?: string): Promise<string | null> {
  if (recovery) return recovery;
  recovery = (async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return null;
    // Another request/tab may already have refreshed the rejected token.
    if (session.access_token !== rejectedToken) return session.access_token;
    const result = await supabase.auth.refreshSession();
    if (
      result.error &&
      (result.error.status === undefined ||
        result.error.status >= 500 ||
        result.error.name === "AuthRetryableFetchError")
    )
      throw new RoutineApiError(
        "Could not refresh your session. Check your connection and retry.",
        503,
      );
    return result.data.session?.access_token ?? null;
  })();
  try {
    return await recovery;
  } finally {
    recovery = null;
  }
}

async function expireSession(): Promise<never> {
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem("br-session-expired", "1");
    } catch {
      /* storage may be disabled */
    }
  }
  // Clear this browser's rejected session, without signing out other devices.
  await supabase.auth.signOut({ scope: "local" });
  throw new RoutineApiError(
    "Your session has expired. Please sign in again.",
    401,
  );
}

export async function routineRequest<T>(
  action: string,
  values: Record<string, unknown> = {},
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const invoke = (token?: string) =>
    supabase.functions.invoke("routine_video", {
      body: { ...values, action },
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    });
  let result = await invoke(session?.access_token);
  // Retry only authentication failures, before the API has performed any action.
  // A real permission denial (403) must never trigger token refresh or sign-out.
  if (
    result.error?.context?.status === 401 &&
    !(action === "get" && values.share_token)
  ) {
    const token = await recoverSession(session?.access_token);
    if (!token) return expireSession();
    result = await invoke(token);
    if (result.error?.context?.status === 401) return expireSession();
  }
  if (result.error) {
    let message = result.error.message;
    try {
      const details = await result.error.context?.json();
      message = details?.error ?? details?.message ?? message;
    } catch {
      /* response may not be JSON */
    }
    const status = result.error.context?.status;
    const local = /^http:\/\/(127\.0\.0\.1|localhost):/.test(
      process.env.PUBLIC_SUPABASE_URL ?? "",
    );
    if (
      local &&
      ([502, 503, 504].includes(status) ||
        result.error.name === "FunctionsFetchError")
    )
      message =
        "Local video API is unavailable. Restart with bun run dev:local, then retry.";
    throw new RoutineApiError(message, status);
  }
  if (result.data?.error) throw new Error(result.data.error);
  return result.data as T;
}
export const getRoutine = (id: string, shareToken?: string) =>
  routineRequest<RoutineSnapshot>("get", {
    playlist_id: id,
    share_token: shareToken,
  });
export const reviewRoutine = (id: string, reviews: RoutineReview[]) =>
  routineRequest<RoutineSnapshot>("review", { playlist_id: id, reviews });
export const claimRoutine = (id: string, fingerprint: string) =>
  routineRequest<RoutineJob | { already_current: true }>("claim", {
    playlist_id: id,
    fingerprint,
  });
export type UploadTicket = { provider?: "supabase"; bucket: string; path: string; token: string }
  | { provider: "vimeo"; upload_url: string; job_id: string };

export async function uploadRoutineFile(
  ticket: UploadTicket,
  file: Blob,
  signal: AbortSignal,
  onProgress: (percent: number) => void,
) {
  const { Upload } = await import("tus-js-client");
  if (ticket.provider === "vimeo") {
    // Vimeo pre-creates the tus session. Never forward Supabase credentials.
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (error?: Error) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener("abort", abort);
        error ? reject(error) : resolve();
      };
      const upload = new Upload(file, {
        uploadUrl: ticket.upload_url,
        chunkSize: 6 * 1024 * 1024,
        retryDelays: [0, 1000, 3000, 5000, 10000],
        onProgress: (sent, total) => onProgress(Math.round(sent / total * 100)),
        onError: () => finish(new Error("Vimeo upload could not finish. Check your connection and retry.")),
        onSuccess: () => finish(),
      });
      const abort = () => { void upload.abort().finally(() => finish(new DOMException("Upload cancelled", "AbortError"))); };
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) abort(); else upload.start();
    });
    return;
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error("Sign in again before uploading.");
  const base = new URL(process.env.PUBLIC_SUPABASE_URL!);
  if (base.hostname.endsWith(".supabase.co"))
    base.hostname = base.hostname.replace(
      ".supabase.co",
      ".storage.supabase.co",
    );
  await new Promise<void>((resolve, reject) => {
    let settled = false;
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      signal.removeEventListener("abort", abort);
      error ? reject(error) : resolve();
    };
    const upload = new Upload(file, {
      endpoint: `${base.origin}/storage/v1/upload/resumable`,
      headers: {
        "x-signature": ticket.token,
        apikey: process.env.PUBLIC_SUPABASE_PUBLISHABLE_KEY!,
      },
      metadata: {
        bucketName: ticket.bucket,
        objectName: ticket.path,
        contentType: file.type || "video/mp4",
        cacheControl: "3600",
      },
      chunkSize: 6 * 1024 * 1024,
      retryDelays: [0, 1000, 3000, 5000, 10000],
      onBeforeRequest: async (request) => {
        const {
          data: { session: current },
        } = await supabase.auth.getSession();
        if (current)
          request.setHeader("Authorization", `Bearer ${current.access_token}`);
      },
      removeFingerprintOnSuccess: true,
      // A distinct object per job prevents collisions between similar Blobs.
      fingerprint: async () =>
        `routine:${base.origin}:${ticket.bucket}:${ticket.path}:${file.size}`,
      onProgress: (sent, total) => onProgress(Math.round((sent / total) * 100)),
      onError: () =>
        finish(
          new Error(
            "Upload could not finish. Check your connection and retry. Your saved reviews are safe.",
          ),
        ),
      onSuccess: () => finish(),
    });
    const abort = () => {
      void upload
        .abort()
        .finally(() =>
          finish(new DOMException("Upload cancelled", "AbortError")),
        );
    };
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) {
      abort();
      return;
    }
    void upload
      .findPreviousUploads()
      .then((previous) => {
        if (signal.aborted) return;
        if (previous[0]) upload.resumeFromPreviousUpload(previous[0]);
        upload.start();
      })
      .catch(finish);
  });
}
